'use client';

import { loadFont } from '@remotion/fonts';
import { useLayoutEffect, useMemo, useState } from 'react';
import {
  cancelRender,
  continueRender,
  delayRender,
  getRemotionEnvironment,
  staticFile,
} from 'remotion';
import {
  buildLocalFontInfo,
  getFontVariants as getFontVariantsFromInfo,
  loadFontInfoFromApi as loadFontInfoFromApiShared,
} from './load-font-info';
import type { LocalFontInfo } from './load-font-info';
import { getFontFamilyFallbacks } from './font-family-css-stack';

// 구글 폰트 API 호출 대신 로컬 woff 자산을 직접 로드하기 위한 유틸

const resolveFontUrl = (sourcePath: string) => {
  if (/^https?:\/\//.test(sourcePath)) {
    return sourcePath;
  }

  const normalized = sourcePath.startsWith('/')
    ? sourcePath.slice(1)
    : sourcePath;

  return staticFile(normalized);
};

export const loadFontInfo = async ({
  fontFamily,
  fontInfosDuringRendering: _fontInfosDuringRendering,
}: {
  fontFamily: string;
  fontInfosDuringRendering: LocalFontInfo | null;
}) => {
  if (getRemotionEnvironment().isRendering) {
    // JSON 직렬화로 인해 getPathByWeight 함수가 손실되었으므로 재생성
    // fontInfosDuringRendering 값은 실제로 사용하지 않고 buildLocalFontInfo로 새로 생성
    return buildLocalFontInfo(fontFamily);
  }

  return loadFontInfoFromApiShared(fontFamily);
};

type LoadFontInfo = {
  fontFamily: string;
  fontVariant?: string;
  fontWeight?: string;
  fontInfosDuringRendering: LocalFontInfo | null;
};

// 동일한 폰트 파일 로딩을 병렬 요청 간에 공유
const loadedFontPromises = new Map<string, Promise<void>>();

// variant-weight 조합까지 포함한 캐시 키 생성
const getFontCacheKey = (family: string, variant: string, weight: string) =>
  `${family}-${variant}-${weight}`;

const loadSingleFontFromTextItem = async (item: LoadFontInfo) => {
  const fontInfo = await loadFontInfo({
    fontFamily: item.fontFamily,
    fontInfosDuringRendering: item.fontInfosDuringRendering,
  });

  const variant = item.fontVariant || 'normal';
  const weight = item.fontWeight || fontInfo.defaultWeight;
  const cacheKey = getFontCacheKey(fontInfo.family, variant, weight);

  if (!loadedFontPromises.has(cacheKey)) {
    // weight에 맞는 파일 경로 조회 (static font는 weight별 다른 파일)
    const fontPath = fontInfo.getPathByWeight(weight);

    loadedFontPromises.set(
      cacheKey,
      loadFont({
        family: fontInfo.family,
        url: resolveFontUrl(fontPath),
        weight,
        style: variant,
      }).catch((error) => {
        loadedFontPromises.delete(cacheKey);
        throw error;
      }),
    );
  }

  await loadedFontPromises.get(cacheKey);
};

// Remotion 텍스트 항목이 요구하는 variant/weight 조합을 로컬 자산으로 로드
export const loadFontFromTextItem = async (item: LoadFontInfo) => {
  const familiesToLoad = [
    item.fontFamily,
    ...getFontFamilyFallbacks(item.fontFamily),
  ];

  await Promise.all(
    familiesToLoad.map((fontFamily) =>
      loadSingleFontFromTextItem({
        ...item,
        fontFamily,
      }),
    ),
  );
};

// 렌더링 훅에서도 동일한 로컬 폰트 로딩 로직을 활용
export const useLoadFontFromTextItem = ({
  fontFamily,
  fontInfosDuringRendering,
  fontVariant,
  fontWeight,
}: LoadFontInfo) => {
  const [loaded, setLoaded] = useState(false);
  const prom = useMemo(() => {
    return loadFontFromTextItem({
      fontFamily,
      fontInfosDuringRendering,
      fontVariant,
      fontWeight,
    });
  }, [fontFamily, fontInfosDuringRendering, fontVariant, fontWeight]);

  useLayoutEffect(() => {
    const handle = delayRender('Loading font');
    prom
      .then(() => {
        continueRender(handle);
        setLoaded(true);
      })
      .catch((err) => {
        cancelRender(err);
      });
  }, [prom, loaded]);

  return loaded;
};

export type { LocalFontInfo } from './load-font-info';
export const loadFontInfoFromApi = loadFontInfoFromApiShared;
export const getFontVariants = getFontVariantsFromInfo;
export { buildLocalFontInfo } from './load-font-info';
